/**
 * Shared formatting helpers so money and dates look the same on every screen
 * (Polish conventions: "3 996,10 zł", "27.12.2025").
 */

const NBSP = ' ';
const MINUS = '−';

const formatters = {
    auto: new Intl.NumberFormat('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 2, useGrouping: 'always' as any }),
    fixed: new Intl.NumberFormat('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: 'always' as any }),
};

interface MoneyOptions {
    /** Always show two decimals (use for balances and totals). Default: only when needed. */
    fixed?: boolean;
    /** Prefix positive amounts with "+" (use for signed differences). */
    signed?: boolean;
}

export function formatMoney(value: number | null | undefined, { fixed = false, signed = false }: MoneyOptions = {}): string {
    const amount = Number(value ?? 0);
    const formatter = fixed ? formatters.fixed : formatters.auto;
    const abs = formatter.format(Math.abs(amount));
    const isZero = Math.abs(amount) < 0.005;
    const sign = isZero ? '' : amount < 0 ? MINUS : signed ? '+' : '';
    return `${sign}${abs}${NBSP}zł`;
}

/** 'negative' | 'zero' | 'positive' — for colouring amounts. */
export function amountTone(value: number | null | undefined): 'negative' | 'zero' | 'positive' {
    const amount = Number(value ?? 0);
    if (Math.abs(amount) < 0.005) return 'zero';
    return amount < 0 ? 'negative' : 'positive';
}

/** '2025-12-27' (or '2025-12-27T10:00:00') → '27.12.2025'. Anything else is returned unchanged. */
export function formatDate(iso: string | null | undefined): string {
    if (!iso) return '';
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    return match ? `${match[3]}.${match[2]}.${match[1]}` : iso;
}

/** Groups an account number in blocks of four so it is easy to read and compare. */
export function formatAccountNumber(account: string): string {
    const compact = account.replace(/\s+/g, '');
    return compact.replace(/(.{4})/g, '$1 ').trim();
}
